// Перенос выбранных вариантов магии на доску. Чистая функция над AppState:
// вариантов может быть несколько, а `addVariant` не возвращает id нового
// варианта — собрать состояние разом честнее, чем угадывать id после диспатча.
// Так же поступают шаблоны поездок (см. src/templates/withTemplate).
import type { AppState, TripVariant } from '../../types';
import { MAX_VARIANTS } from '../../types';
import { VARIANT_COLORS, newId } from '../../store';
import type { Translate } from '../../i18n';

export interface MagicPick {
  name: string;
  segmentIds: readonly string[];
}

/**
 * Добавляет варианты в конец доски.
 *
 * @param limit сколько вариантов всего разрешено этому пользователю
 *   (у гостя меньше, чем MAX_VARIANTS — предел приходит из auth).
 *   Всё, что не влезло, молча не добавляется: сколько влезет, интерфейс
 *   считает заранее и показывает человеку до запуска.
 */
export function applyMagicVariants(
  state: AppState,
  picks: readonly MagicPick[],
  limit: number,
  t: Translate,
): AppState {
  // Персональный лимит может быть и выше MAX_VARIANTS — его выдаёт админка,
  // а проверяет сервер; здесь ему верим как есть.
  const ceiling = Number.isFinite(limit) ? limit : MAX_VARIANTS;
  const room = Math.max(0, ceiling - state.variants.length);
  if (room === 0 || picks.length === 0) return state;

  const added: TripVariant[] = [];
  for (const pick of picks) {
    if (added.length >= room) break;

    // Блок мог исчезнуть из библиотеки, пока человек читал варианты, — id без
    // блока оставил бы в варианте дырку, которой не видно на таймлайне.
    const seen = new Set<string>();
    const segmentIds = pick.segmentIds.filter((id) => {
      if (seen.has(id) || !state.library[id]) return false;
      seen.add(id);
      return true;
    });
    if (segmentIds.length === 0) continue;

    const position = state.variants.length + added.length;
    added.push({
      id: newId('v'),
      name: pick.name.trim() || t('variant.defaultName', { n: position + 1 }),
      color: VARIANT_COLORS[position % VARIANT_COLORS.length] ?? '#5b7c99',
      segmentIds,
    });
  }

  if (added.length === 0) return state;
  return { ...state, variants: [...state.variants, ...added] };
}
